// Step 2: drop the folder, watch it process, see every render placed.
import { PLAN } from '@archviewvr/config';
import { getProject, getPanoramas, getLevels, draftUrl, type Project, type PanoRow } from '../data';
import { getWork, newWork, dropWork, processFiles, storageOf, type Base, type Work, type WorkPano, type Step, type WorkEvent } from '../process';
import { modelFormatOf } from '@archviewvr/engine';
import { html, icon, icons, $, flowHeader, formatBytes, formatLimit, toast, confirmDialog, choiceDialog } from '../ui';
import { openTutorial } from '../tutorial/player';

/** Reads every file in a drop, including inside folders. */
async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  const entries = Array.from(dt.items).map((i) => i.webkitGetAsEntry?.()).filter(Boolean) as FileSystemEntry[];
  if (!entries.length) return Array.from(dt.files);
  const out: File[] = [];
  const walk = async (e: FileSystemEntry): Promise<void> => {
    if (e.isFile) out.push(await new Promise<File>((res, rej) => (e as FileSystemFileEntry).file(res, rej)));
    else if (e.isDirectory) {
      const reader = (e as FileSystemDirectoryEntry).createReader();
      let batch: FileSystemEntry[];
      do {
        batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        for (const c of batch) await walk(c);
      } while (batch.length);
    }
  };
  for (const e of entries) await walk(e);
  return out;
}

const badge = (state: Step['state']) => {
  if (state === 'ok') return html`<span class="badge ok">${icon('tick', 13)}</span>`;
  if (state === 'busy') return html`<span class="badge busy">${icon('spin', 15)}</span>`;
  if (state === 'warn') return html`<span class="badge warn">!</span>`;
  if (state === 'bad') return html`<span class="badge bad">×</span>`;
  return html`<span class="badge wait">·</span>`;
};

const mark = (p: WorkPano) => {
  if (p.status === 'ok') return `<span class="mark ok" aria-label="Ready"><span class="ic">${icons.tick}</span></span>`;
  if (p.status === 'busy') return `<span class="mark busy" aria-label="Processing"><span class="ic">${icons.spin}</span></span>`;
  if (p.status === 'warn') return '<span class="mark warn" aria-label="Check this one">!</span>';
  return '<span class="mark bad" aria-label="Not used">×</span>';
};

export async function uploadView(root: HTMLElement, projectId: string): Promise<void> {
  const project = await getProject(projectId);
  if (!project) { root.innerHTML = html`<div class="loading-page">Project not found. <a href="#/">Back to projects</a></div>`.html; return; }
  const existing = getWork(projectId);
  if (existing && (existing.running || existing.done)) renderWork(root, project, existing);
  else renderDrop(root, project, await getPanoramas(projectId), (await getLevels(projectId)).map((l) => +l.height));
}

/** The saved project, so a drop can add renders, overwrite same-named ones or swap the model. */
export const baseOf = (project: Project, saved: PanoRow[], levels: number[]): Base | null => (saved.length ? {
  levels,
  modelUnit: Number((project.settings as { modelUnit?: number } | undefined)?.modelUnit) || undefined,
  depthModel: !!(project.settings as { depthModel?: boolean } | undefined)?.depthModel,
  modelUrl: draftUrl(project.id, project.model_key ?? 'model.glb'),
  panos: saved.map((p) => ({
    id: p.id, name: p.name, x: +p.x, y: +p.y, z: +p.z, yaw: +p.yaw, room: p.room,
    thumbUrl: draftUrl(project.id, `thumbs/${p.id}.jpg`), imageUrl: draftUrl(project.id, p.mobile_key ?? p.base_key),
  })),
} : null);

function renderDrop(root: HTMLElement, project: Project, saved: PanoRow[] = [], savedLevels: number[] = []): void {
  const updating = saved.length > 0;
  root.innerHTML = html`<div class="flow">
    ${flowHeader({ back: `#/p/${project.id}`, eyebrow: project.state === 'published' ? 'Update renders' : 'New project', title: project.name, step: project.state === 'published' ? undefined : 2, project: project.id, canCheck: !!getWork(project.id)?.done })}
    <div class="flow-body">
      <label class="drop" id="drop" style="flex:1 1 100%" for="pick-files">
        <div>
          <div class="big">${icon('folder', 30)}</div>
          <h2>${updating ? 'Drop new or updated files here' : 'Drop your project folder here'}</h2>
          <p>${updating
            ? `Only what changed: renders with the same name replace the saved ones, new names are added, and a new model or points file replaces the old one. Hotspots, room names, the start view and settings stay as they are. The ${saved.length} saved renders you don’t drop are kept.`
            : 'Everything at once: the proxy model, the camera points file from 3ds Max and all the renders. It’s processed here in your browser; nothing uploads until you’ve checked it.'}</p>
          <div class="picks">
            <span class="btn primary" id="pick-folder-btn">Choose folder</span>
            <span class="btn">Choose files</span>
          </div>
          <div class="need"><span><b>Model</b> .glb, .gltf or .obj</span><span><b>Points</b> .txt or .json</span><span><b>Renders</b> 2:1 JPEG or PNG</span><span><b>Depth maps</b> optional, CZDepth .exr</span></div>
        </div>
      </label>
      <p class="drop-help">Not sure what goes in the folder? <button class="link-btn inline" id="guide-folder">See the guide</button></p>
      <input type="file" id="pick-files" multiple hidden accept=".obj,.glb,.gltf,.txt,.csv,.json,.exr,image/jpeg,image/png,image/webp">
      <input type="file" id="pick-folder" hidden webkitdirectory>
    </div>
  </div>`.html;
  $('#guide-folder', root).onclick = () => { void openTutorial({ startAt: 'folder' }); };
  const drop = $('#drop', root);
  const start = async (files: File[]) => {
    if (!files.length) return;
    const base = baseOf(project, saved, savedLevels);
    // depth maps but no model file in an update: keep the saved model, or make it again from them
    if (base && files.some((f) => /\.exr$/i.test(f.name)) && !files.some((f) => modelFormatOf(f.name))) {
      const pick = await choiceDialog<'remake' | 'keep'>({
        title: 'Make the model again from these depth maps?',
        body: html`<p>There's no model file in this drop, but there are depth maps.</p>`,
        choices: [
          { value: 'remake', label: 'Make it again', note: 'A new model from the depth maps, with the latest clean-up. Best if the saved one was made from depth maps.', primary: !!base.depthModel },
          { value: 'keep', label: 'Keep the saved model', note: 'The depth maps are added to the renders, fitted to the saved model.', primary: !base.depthModel },
        ],
      });
      if (!pick) return;
      base.depthModel = pick === 'remake';
    }
    const w = newWork(project.id);
    w.base = base;
    renderWork(root, project, w);
    processFiles(w, files).catch((e) => { console.error(e); toast('Processing stopped: ' + (e instanceof Error ? e.message : 'unknown error'), 'error', 8000); });
  };
  drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('over'); });
  drop.addEventListener('dragleave', () => drop.classList.remove('over'));
  drop.addEventListener('drop', async (e) => { e.preventDefault(); drop.classList.remove('over'); void start(await filesFromDrop(e.dataTransfer!)); });
  $('#pick-folder-btn', root).addEventListener('click', (e) => { e.preventDefault(); $<HTMLInputElement>('#pick-folder', root).click(); });
  for (const id of ['#pick-files', '#pick-folder']) {
    const input = $<HTMLInputElement>(id, root);
    input.onchange = () => { start(Array.from(input.files ?? [])); input.value = ''; };
  }
}

function renderWork(root: HTMLElement, project: Project, w: Work): void {
  const trial = project.state === 'trial';
  root.innerHTML = html`<div class="flow">
    ${flowHeader({ back: `#/p/${project.id}`, eyebrow: trial ? 'New project' : 'Update renders', title: project.name, step: project.state === 'published' ? undefined : 2, project: project.id, canCheck: !!getWork(project.id)?.done })}
    <div class="flow-body">
      <section class="col-l">
        <div><h1 style="font-size:24px">Reading your folder</h1><p class="muted" style="margin-top:6px;line-height:1.55">Everything is processed in your browser. Nothing uploads until you’ve checked it.</p></div>
        <ul class="checklist" id="steps"></ul>
        <div class="box storage" id="storage"></div>
        <div class="preview3d" id="pv"><div class="wait" id="pv-wait">The 3D plan appears when the renders are placed</div><span class="cap" id="pv-cap" hidden>Your model, painted from your renders. Drag to spin.</span></div>
        <details class="explain"><summary>How the 3D plan is made</summary>
          <p>There’s nothing extra to render or process. Your proxy model is painted live from your renders: each surface takes its colour from the closest render that can see it, and a depth check stops colour leaking through walls.</p>
          <p>Grey patches are surfaces no render can see, like the top of a cupboard or a closed room. Add a render there, or leave that part out of the proxy model.</p>
          <p>It’s rebuilt whenever you save renders, so a new model or re-render shows up straight away.</p>
        </details>
      </section>
      <section class="col-r">
        <div class="grid-head"><h2>Renders</h2><div class="counts" id="counts"></div></div>
        <div class="thumbs" id="tiles"></div>
      </section>
    </div>
    <footer class="flow-foot">
      <span class="muted" style="font-size:13px" id="foot-note"></span>
      <div class="btn-row">
        <button class="btn" id="start-over">Drop different files</button>
        <a class="btn primary" id="go-check" href="#/p/${project.id}/check">Continue to check</a>
      </div>
    </footer>
  </div>`.html;

  const tiles = $('#tiles', root);
  const tileHtml = (p: WorkPano) => `<figure class="tile" data-id="${p.id}"><div class="img">${p.thumbUrl ? `<img src="${p.thumbUrl}" alt="">` : ''}</div>${mark(p)}<figcaption><span>${p.name}</span><em>${p.note || (p.level != null ? (p.level === 0 ? 'Ground' : `Level ${p.level}`) : '')}</em></figcaption></figure>`;
  tiles.innerHTML = w.panos.map(tileHtml).join('');

  const drawSteps = () => {
    $('#steps', root).innerHTML = w.steps.map((s) => html`<li>${badge(s.state)}<div style="min-width:0;flex:1 1 auto"><div class="t">${s.title}</div><div class="d">${s.detail}</div>${s.progress != null ? html`<div class="bar"><i style="width:${(s.progress * 100).toFixed(0)}%"></i></div>` : ''}</div></li>`.html).join('');
  };
  const drawStorage = () => {
    const st = storageOf(w);
    const lim = project.storage_limit || PLAN.storageLimitBytes;
    const pct = (n: number) => Math.min(100, (n / lim) * 100).toFixed(1);
    $('#storage', root).innerHTML = html`<div class="top"><span>Storage</span><span class="mono">${formatBytes(st.total)} / ${formatLimit(lim)}</span></div>
      <div class="bar"><i style="width:${pct(st.renders)}%"></i><i style="width:${pct(st.model)}%;background:#44c7f4"></i></div>
      <div class="legend"><span><i style="background:var(--accent)"></i>Renders</span><span><i style="background:#44c7f4"></i>Model</span></div>
      ${st.total > lim ? html`<div class="err">Over this project’s ${formatLimit(lim)} limit. Remove some renders or lower their size, or <a href="mailto:${PLAN.supportEmail}?subject=${encodeURIComponent(`More storage for ${project.name ?? 'my project'}`)}">contact us for more space</a>.</div>` : ''}`.html;
  };
  const drawCounts = () => {
    const c = { ok: 0, busy: 0, warn: 0, bad: 0 };
    w.panos.forEach((p) => c[p.status]++);
    $('#counts', root).innerHTML = [
      c.ok ? `<span><i style="background:var(--good)"></i>${c.ok} ready</span>` : '',
      c.busy ? `<span><i style="background:var(--accent)"></i>${c.busy} processing</span>` : '',
      c.warn ? `<span><i style="background:var(--warn)"></i>${c.warn} to check</span>` : '',
      c.bad ? `<span><i style="background:var(--bad)"></i>${c.bad} not used</span>` : '',
    ].join('');
    const usable = w.panos.filter((p) => p.ep).length;
    $('#foot-note', root).textContent = trial && usable > PLAN.trialPanoLimit
      ? `Trial projects save up to ${PLAN.trialPanoLimit} renders. Publishing unlocks all ${usable}.`
      : `${usable} render${usable === 1 ? '' : 's'} ready`;
  };
  // throw this drop away and pick files again
  $<HTMLButtonElement>('#start-over', root).onclick = async () => {
    if (w.running) return;
    if (w.done && !(await confirmDialog({ title: 'Drop different files?', body: 'What’s been read so far is cleared. Nothing saved in the project changes.', confirm: 'Drop different files' }))) return;
    dropWork(project.id);
    renderDrop(root, project, await getPanoramas(project.id), (await getLevels(project.id)).map((l) => +l.height));
  };
  const drawFooter = () => {
    const over = $<HTMLButtonElement>('#start-over', root);
    over.disabled = w.running;
    over.title = w.running ? 'Wait until the folder has been read' : '';
    const go = $<HTMLAnchorElement>('#go-check', root);
    const ok = w.done && !w.failed;
    go.classList.toggle('disabled', !ok);
    go.toggleAttribute('aria-disabled', !ok);
    go.style.opacity = ok ? '' : '.5';
    go.style.pointerEvents = ok ? '' : 'none';
    if (w.failed) {
      const n = $('#foot-note', root);
      n.innerHTML = html`<span class="err">Fix the item marked × and drop the folder again.</span> <a href="#/p/${project.id}/upload" id="retry">Start again</a>`.html;
      $('#retry', root).onclick = async (e) => { e.preventDefault(); renderDrop(root, project, await getPanoramas(project.id), (await getLevels(project.id)).map((l) => +l.height)); };
    }
  };

  // live 3D plan preview
  const pv = $('#pv', root);
  pv.prepend(w.canvas);
  const size = () => { const r = pv.getBoundingClientRect(); if (r.width) w.engine.resize(r.width, r.height); };
  const ro = new ResizeObserver(size); ro.observe(pv); size();
  const showPlan = () => { $('#pv-wait', root).hidden = w.done; $('#pv-cap', root).hidden = !w.done; };
  w.engine.start();
  attachOrbit(w, w.canvas);

  const onEvent = (e: WorkEvent) => {
    if (!root.contains(tiles)) { w.listeners.delete(onEvent); ro.disconnect(); return; }
    if (e.kind === 'step') drawSteps();
    else if (e.kind === 'storage') drawStorage();
    else if (e.kind === 'pano') {
      const el = $(`[data-id="${e.pano.id}"]`, tiles);
      if (el) el.outerHTML = tileHtml(e.pano);
      else tiles.insertAdjacentHTML('beforeend', tileHtml(e.pano));
      drawCounts();
    } else if (e.kind === 'done') {
      drawSteps(); drawCounts(); drawFooter(); showPlan();
      tiles.innerHTML = w.panos.map(tileHtml).join(''); // levels and notes are known now
    }
  };
  w.listeners.add(onEvent);
  drawSteps(); drawStorage(); drawCounts(); drawFooter(); showPlan();
}

/** Drag to spin, wheel or pinch to zoom, for the 3D plan previews. */
export function attachOrbit(w: Work, canvas: HTMLCanvasElement): void {
  if ((canvas as any)._orbit) return;
  (canvas as any)._orbit = true;
  let last: { x: number; y: number } | null = null;
  canvas.addEventListener('pointerdown', (e) => { last = { x: e.clientX, y: e.clientY }; canvas.setPointerCapture(e.pointerId); w.engine.touchOrbit(); });
  canvas.addEventListener('pointermove', (e) => {
    if (!last) return;
    const dx = e.clientX - last.x, dy = e.clientY - last.y;
    last = { x: e.clientX, y: e.clientY };
    if (w.engine.mode === 'doll') w.engine.orbitBy(dx, dy, e.shiftKey || e.buttons === 2);
    else w.engine.look(dx, dy, canvas.clientHeight || 400);
  });
  canvas.addEventListener('pointerup', () => { last = null; });
  canvas.addEventListener('pointercancel', () => { last = null; });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener('wheel', (e) => { e.preventDefault(); w.engine.zoom(Math.exp(e.deltaY * 0.001)); }, { passive: false });
}

